import React, { useContext, useMemo, useState } from 'react';
import { Company, CompanyStatus } from '../../../../types/company';
import { FileEntityType } from '../../../../types/file';
import { FileViewLoader } from '../../../../VentoryUI/components/common/File';
import { VentoryColor } from '../../../../VentoryUI/util/color.util';
import { ReactComponent as VentoryWhiteLogo } from '../../../../assets/img/Ventory-WhiteNoBack.svg';
import DuplicateCompanyModal from '../../Settings/Security/CompanySuperUserSettings/DuplicateCompanyModal';
import { CompanyContext } from '../../../../context/CompanyContext';
import { useNavigate } from 'react-router';
import { Dropdown, DropdownList } from '../../../components/Dropdown';
import { IconButton } from '../../../icons';
import { IconType } from '../../../icons/Icon';
import { translate } from '../../../../types/translation/Translator';
import { KANBAN_STATUSES } from './AdministratorCustomerPane';
import { LoadingSpinner } from '../../../components/LoadingSpinner';

interface KanbanCompanyItemProps {
  company: Company;
  onStatusChange: (company: Company, status: CompanyStatus) => void;
  onDragStart: (company: Company) => void;
  onDragEnd: () => void;
  loading: boolean;
}

export default function KanbanCompanyItem({
  company,
  onStatusChange,
  onDragStart,
  onDragEnd,
  loading,
}: KanbanCompanyItemProps) {
  const { refreshCompany } = useContext(CompanyContext);
  const navigate = useNavigate();

  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const logo = useMemo(() => {
    if (company.settings.companyLogo) {
      return (
        <FileViewLoader
          className='object-scale-down'
          fileId={company.settings.companyLogo}
          fileEntityType={FileEntityType.company}
          companyId={company.id}
        />
      );
    }

    return <VentoryWhiteLogo color={VentoryColor.grey500} stroke={VentoryColor.grey500} />;
  }, []);

  const menuItems = (() => {
    const items: { text: string; onClick: () => void }[] = [
      {
        text: 'Duplicate',
        onClick: () => setDuplicateModalOpen(true),
      },
    ];

    KANBAN_STATUSES.filter(s => s !== company.status).forEach(s => {
      items.push({
        text: `Move to ${translate({ key: s as any })}`,
        onClick: () => onStatusChange(company, s),
      });
    });

    return items;
  })();

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(true);
    onDragStart(company);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDragEnd();
  };

  const handleClick = () => {
    refreshCompany(company.id);
    navigate('/dashboard');
  };

  return (
    <>
      {duplicateModalOpen && (
        <DuplicateCompanyModal company={company} open={duplicateModalOpen} setOpen={setDuplicateModalOpen} />
      )}

      {loading ? (
        <div className='flex min-h-[170px] min-w-[170px] items-center justify-center rounded-2 bg-white shadow-default'>
          <LoadingSpinner />
        </div>
      ) : (
        <div
          draggable
          onClick={handleClick}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          className={`relative grid max-h-[170px] min-h-[170px] min-w-[170px] max-w-[170px] select-none rounded-2 bg-white p-4 shadow-default transition hover:shadow-hover ${
            isDragging ? 'scale-[1.02] cursor-grabbing opacity-70' : 'cursor-grab'
          }`}
        >
          <div className='flex flex-col items-center justify-center gap-2'>
            <div
              className='flex h-[40px] w-full items-center justify-center'
              style={{ backgroundColor: company.settings.headerColor }}
            >
              {logo}
            </div>
            <p className='line-clamp-2 text-center font-medium text-black'>{company.name}</p>
          </div>

          <div className='absolute bottom-0 right-0'>
            <Dropdown
              width={240}
              open={menuOpen}
              setOpen={setMenuOpen}
              dropdown={
                <DropdownList
                  items={menuItems}
                  textBuilder={mu => mu.text}
                  onClick={mu => {
                    mu.onClick();
                    setMenuOpen(false);
                  }}
                />
              }
            >
              <IconButton
                type={IconType.dotsVertical}
                onClick={event => {
                  event.stopPropagation();
                  setMenuOpen(open => !open);
                }}
              />
            </Dropdown>
          </div>
        </div>
      )}
    </>
  );
}
